/**
 * Keeps an engineer's draft per chat session in sessionStorage, so closing and reopening the editor does not lose it.
 * Every access is guarded: with storage missing, blocked or full the editor works the same, it just forgets on close.
 */
const KEY_PREFIX = "arta.learningDraft.";
const VERSION = 1;

const keyOf = (sessionId) => `${KEY_PREFIX}${sessionId}`;
const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const isList = (v) => Array.isArray(v) && v.every((x) => typeof x === "string");

const storage = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

function valid(saved) {
  return isObject(saved) && saved.version === VERSION && isObject(saved.draft)
    && isList(saved.draft.symptoms) && isList(saved.draft.steps) && isObject(saved.baseline);
}

/** The saved {draft, baseline, truncated, category} for this session, or null (nothing saved, unreadable or storage unavailable). */
export function loadDraft(sessionId) {
  const store = storage();
  if (!store || !sessionId) return null;
  try {
    const saved = JSON.parse(store.getItem(keyOf(sessionId)));
    return valid(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function saveDraft(sessionId, { draft, baseline, truncated, category }) {
  const store = storage();
  if (!store || !sessionId) return;
  try {
    store.setItem(keyOf(sessionId), JSON.stringify({ version: VERSION, draft, baseline, truncated, category }));
  } catch {
    // storage full or blocked: the draft just is not kept
  }
}

export function clearDraft(sessionId) {
  const store = storage();
  if (!store || !sessionId) return;
  try {
    store.removeItem(keyOf(sessionId));
  } catch {
    // nothing to clear
  }
}
